import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  useCookies,
  removeToken,
  getToken,
  getBearer,
  setToken as setTokenCookie,
  takeStashedToken,
  restoreStashedCookies,
  isReloadOf,
  isLoginClaims,
  TokenType,
  type TypeClaims
} from '@/lib/auth'
import { renew } from '@/gen/oas/authentication'
import { setFaroUser } from '@/lib/faro-hook'

import { useMutation } from '@tanstack/vue-query'
import { logoutMutation } from '@/gen/oas/authentication/@tanstack/vue-query.gen'

const WEBAPP_LOGOUT_TIMEOUT_MS = 3000

export const useAuthStore = defineStore('auth', () => {
  const claims = ref<TypeClaims | null>(null)
  const token = ref<string | null>(null)
  const cookies = useCookies()

  const lastLoginIdentity = ref<{ provider: string; categoryId: string } | null>(null)

  // Track cookie changes
  let previousCookieValue = getBearer(cookies)

  const isAuthenticated = computed(() => !!token.value)
  const tokenType = computed(() => claims.value?.type)
  const sessionId = computed(() => claims.value?.session_id || null)
  const user = computed(() => claims.value?.data || null)

  // Keep Faro telemetry in sync with the auth state automatically — avoids
  // having to remember to call setFaroUser on every code path that mutates
  // claims (login, renew, category-select, logout, external cookie clear…).
  watch(
    claims,
    (newClaims) => {
      if (newClaims && isLoginClaims(newClaims)) {
        setFaroUser({
          id: newClaims.data.user_id,
          role: newClaims.data.role_id,
          sessionId: newClaims.session_id
        })
        lastLoginIdentity.value = {
          provider: newClaims.data.provider,
          categoryId: newClaims.data.category_id
        }
      } else {
        setFaroUser(null)
      }
    },
    { immediate: true }
  )

  // Cookie watcher - only handles auth state, not session management
  const cookieWatcher = watch(
    () => getBearer(cookies),
    async (newValue, oldValue) => {
      console.debug('🍪 Cookie watcher triggered:', {
        newValue,
        oldValue,
        previousCookieValue
      })

      // --- CASE 1: Cookie removed → external logout ---
      if (!newValue && previousCookieValue) {
        console.debug('🔒 Session cookie deleted → external logout detected')
        const tokenForLogout = previousCookieValue
        previousCookieValue = undefined

        // Before resetting auth state, call authentication logout endpoint
        void logoutAuthentication(tokenForLogout).then(followLogoutRedirect)

        // Clear auth state without clearing cookie (already gone)
        $reset()

        // The session store watcher will detect this change and handle the external logout
        return
      }

      // --- CASE 2: Cookie added/updated → reinitialize ---
      if (newValue && newValue !== previousCookieValue) {
        console.debug('🔄 Session cookie changed → reinitialize auth')
        previousCookieValue = newValue
        initialize()
      }
    },
    { immediate: false, flush: 'post' }
  )

  const initialize = () => {
    try {
      const bearer = getBearer(cookies)
      const parsedToken = getToken(cookies)

      if (!bearer || !parsedToken) {
        console.debug('🔓 No valid token, clearing auth state')
        $reset()
        return
      }

      token.value = bearer
      claims.value = parsedToken
      previousCookieValue = bearer

      console.debug('🔐 Auth initialized successfully')
    } catch (error) {
      console.error('❌ Auth initialization failed:', error)
      void logout()
    }
  }

  // Each useCookies() call builds its own universal-cookie instance, so a token
  // written from a view never reaches the watcher above until universal-cookie's
  // 300ms poll notices it. Callers that navigate in-app straight after would hit
  // the router guard with a stale token type, so refresh eagerly here instead.
  const setToken = (bearer: string) => {
    setTokenCookie(cookies, bearer)
    initialize()
  }

  const restoreStashedTokenOnBoot = () => {
    const stashed = takeStashedToken()
    if (!stashed || !isReloadOf(stashed) || getBearer(cookies)) {
      return
    }

    if (restoreStashedCookies(cookies, stashed)) {
      initialize()
    }
  }

  let renewInFlight: Promise<void> | null = null

  const renewSession = async (): Promise<void> => {
    if (renewInFlight) {
      return renewInFlight
    }

    renewInFlight = (async () => {
      if (!token.value) {
        throw new Error('No token available for renewal')
      }

      const response = await renew({
        body: { token: token.value }
      })

      if (!response || response.error) {
        throw new Error(response?.error?.msg || 'Token renewal failed')
      }

      console.debug('✅ Token renewed, updating cookie')
      setToken(response.data.token)
    })().finally(() => {
      renewInFlight = null
    })

    return renewInFlight
  }

  const isLoggingOut = ref(false)

  const logout = async (clearCookie = true): Promise<boolean> => {
    if (isLoggingOut.value) {
      console.debug('⚠️ Logout already in progress')
      return false
    }

    if (!isAuthenticated.value) {
      console.debug('⚠️ Logout called but user is not authenticated')
      return false
    }

    isLoggingOut.value = true
    let redirecting = false

    try {
      const currentToken = token.value
      const currentUser = user.value
      const currentTokenType = claims.value?.type
      const currentExp = claims.value?.exp

      // Clear auth state and cookie immediately
      if (clearCookie) {
        removeToken(cookies)
      }
      token.value = null
      claims.value = null
      previousCookieValue = undefined

      // Intermediate tokens (disclaimer-acknowledgement-required, register,
      // email-verification-required, etc.) have no backend session to revoke
      // and /authentication/logout only accepts login tokens.
      if (currentToken && currentTokenType === TokenType.Login) {
        const webappLogout = ['manager', 'admin'].includes(currentUser?.role_id || '')
          ? logoutWebapp(currentToken)
          : undefined
        if (currentExp && currentExp * 1000 > Date.now()) {
          const redirect = await logoutAuthentication(currentToken)
          await webappLogout
          redirecting = followLogoutRedirect(redirect)
        }
      }

      return redirecting
    } finally {
      if (!redirecting) {
        isLoggingOut.value = false
      }
    }
  }

  // TODO: This should be done by authentication
  const logoutWebapp = async (authToken: string): Promise<void> => {
    console.debug('🚪 Webapp logout initiated')
    await fetch('/isard-admin/logout/remote', {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(WEBAPP_LOGOUT_TIMEOUT_MS)
    })
      .then(() => console.debug('✅ Webapp logout successful'))
      .catch((error) => console.error('❌ Webapp logout failed:', error))
  }

  const { mutateAsync: logoutAuth } = useMutation({
    ...logoutMutation(),
    onSuccess: () => console.debug('✅ Auth service logout successful'),
    onError: (error) => console.error('❌ Auth service logout failed:', error)
  })

  const logoutAuthentication = async (authToken?: string): Promise<string | undefined> => {
    const bearer = authToken || token.value
    if (!bearer) {
      return undefined
    }

    console.debug('🚪 Auth service logout initiated')
    try {
      const data = await logoutAuth({
        body: {},
        headers: { Authorization: `Bearer ${bearer}` }
      })
      return data?.redirect
    } catch {
      return undefined
    }
  }

  const followLogoutRedirect = (redirect?: string): boolean => {
    if (!redirect) {
      return false
    }

    try {
      const url = new URL(redirect, window.location.origin)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return false
      }

      console.debug('↪️ Following the authentication logout redirect:', url.href)
      window.location.assign(url.href)
      return true
    } catch {
      return false
    }
  }

  const $reset = () => {
    token.value = null
    claims.value = null
    previousCookieValue = undefined
  }

  const cleanup = () => {
    cookieWatcher()
  }

  return {
    token,
    claims,
    tokenType,
    sessionId,
    user,
    lastLoginIdentity,
    isAuthenticated,
    initialize,
    setToken,
    restoreStashedTokenOnBoot,
    renewSession,
    logout,
    cleanup,
    $reset
  }
})
